import React, { useState, useEffect } from 'react';
import { useInterface } from '../contexts/InterfaceContext';
import { useProductionMode } from '../contexts/ProductionModeContext';
import { getApiUrl } from '../utils/api';

interface ProductionStatusData {
  currentTime: string;
  currentDateTime: string;
  productionDate: string;
  currentShift: {
    id: number;
    number: number;
    name: string;
    startTime: string | null;
    endTime: string | null;
    duration: { hours: number; minutes: number; seconds: number; totalMinutes: number; totalSeconds: number } | null;
    timeIntoShift: { hours: number; minutes: number; seconds: number; totalMinutes: number; totalSeconds: number } | null;
    timeRemaining: { hours: number; minutes: number; seconds: number; totalMinutes: number; totalSeconds: number } | null;
    isActive: boolean;
  };
  timestamp: string;
}

interface ProductGroupSession {
  id: string;
  productGroupId: string;
  groupId: string;
  groupName: string;
  lineId?: string;
  lineName?: string;
  shiftNumber: number;
  productionTimeSeconds: number;
  prepareTimeSeconds: number;
  cleanTimeSeconds: number;
  stopTimeSeconds: number;
  currentMode: string;
  isActive: boolean;
}

interface ProductionStatusProps {
  productGroup?: any;
  lineMode?: string;
  currentShift?: any;
  productionDate?: string;
  currentTime?: string;
}

const ProductionStatus: React.FC<ProductionStatusProps> = (props) => {
  const { selectedLineId } = useInterface();
  const { getMode, isConnected } = useProductionMode();
  const [status, setStatus] = useState<ProductionStatusData | null>(null);
  // Use props if provided, otherwise fetch data
  const [lineMode, setLineMode] = useState<string>(props.lineMode || '');
  const [activeProductGroup, setActiveProductGroup] = useState<ProductGroupSession | null>(props.productGroup || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  const apiUrl = getApiUrl();
  
  // Don't use context mode initially - let the data fetching set the correct mode
  // Context mode will be updated via WebSocket when changes occur
  useEffect(() => {
    if (isConnected) {
      const contextMode = getMode(selectedLineId);
      // Only update if we have a valid mode from context and it's different
      if (contextMode && contextMode !== 'stop' && contextMode !== lineMode) {
        console.log('ProductionStatus: Updating from context mode:', contextMode);
        setLineMode(contextMode);
      }
    }
  }, [selectedLineId, getMode, isConnected]);

  const fetchProductionStatus = async () => {
    try {
      const response = await fetch(`${apiUrl}/production-status`);
      const result = await response.json();
      
      if (result.message === 'success') {
        setStatus(result.data);
        setError('');
      } else {
        setError('Failed to fetch production status');
      }
    } catch (err) {
      setError('Connection error');
      console.error('Error fetching production status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveProductGroup = async (shiftNumber?: number) => {
    try {
      // Use provided shift number or fall back to status shift
      const shift = shiftNumber || status?.currentShift?.number;
      
      if (shift) {
        // Include lineId if selected to get the correct product group
        const url = selectedLineId 
          ? `${apiUrl}/product-group-sessions/active?shift=${shift}&lineId=${selectedLineId}&updateMetrics=true`
          : `${apiUrl}/product-group-sessions/active?shift=${shift}&updateMetrics=true`;
        console.log('ProductionStatus fetching from URL:', url);
        console.log('ProductionStatus selectedLineId:', selectedLineId);
        console.log('ProductionStatus shift:', shift);
        const response = await fetch(url);
        const result = await response.json();
        console.log('ProductionStatus API response:', result);
        if (result.message === 'success' && result.data) {
          // Debug logging
          console.log('ProductionStatus received:', result.data.lineName, result.data.groupName, 'Mode:', result.data.currentMode, result.data.productionTimeSeconds, 'seconds');
          // Force state update by creating a new object - CRITICAL for React re-rendering
          const newProductGroup = {...result.data};
          setActiveProductGroup(newProductGroup);
          console.log('ProductionStatus: Updated productGroup with productionTime:', newProductGroup.productionTimeSeconds || newProductGroup.productionTime || 0);
          
          // Always update mode from the database - this is the source of truth
          const newMode = result.data.currentMode || 'stop';
          console.log('ProductionStatus: Setting mode from', lineMode, 'to', newMode);
          setLineMode(newMode);
        } else {
          console.log('ProductionStatus: No active product group');
          setActiveProductGroup(null);
          // No active session means stop mode
          setLineMode('stop');
        }
      } else {
        // Don't set to stop if we're still loading shift data
        console.log('ProductionStatus: No shift available yet, loading:', loading);
        if (!loading) {
          // Only set to stop if we're sure there's no shift
          setActiveProductGroup(null);
          setLineMode('stop');
        }
      }
    } catch (error) {
      console.error('Error fetching active Product Group:', error);
      // Don't change mode on error - keep previous state
    } finally {
      // Mark loading as false after first fetch
      if (loading) {
        setLoading(false);
      }
    }
  };

  // Mode is now determined from the active product group's line
  // No need for a separate fetchSystemMode function

  // CRITICAL: Watch for prop changes and force re-render
  useEffect(() => {
    // If props are provided, use them instead of fetching
    if (props.productGroup || props.lineMode) {
      if (props.productGroup) {
        // Force update by creating new object - fixes production timer not updating
        const newProductGroup = {...props.productGroup};
        setActiveProductGroup(newProductGroup);
        console.log('ProductionStatus: Updated from props with productionTime:', newProductGroup.productionTimeSeconds || newProductGroup.productionTime || 0);
      }
      if (props.lineMode) {
        setLineMode(props.lineMode);
      }
      if (props.currentShift && props.productionDate && props.currentTime) {
        setStatus({
          currentTime: props.currentTime,
          currentDateTime: props.currentTime,
          productionDate: props.productionDate,
          currentShift: {
            ...props.currentShift,
            id: props.currentShift.number || 1,
            isActive: props.currentShift.isActive !== undefined ? props.currentShift.isActive : true,
            startTime: props.currentShift.startTime || null,
            endTime: props.currentShift.endTime || null,
            duration: props.currentShift.duration || null,
            timeIntoShift: props.currentShift.timeIntoShift || null,
            timeRemaining: props.currentShift.timeRemaining || null
          },
          timestamp: new Date().toISOString()
        });
      }
      setLoading(false);
      return; // Don't fetch if props are provided
    }
    
    // Original fetching logic for when used without props
    const initializeComponent = async () => {
      try {
        // First get the production status (includes shift info)
        const statusResponse = await fetch(`${apiUrl}/production-status`);
        const statusResult = await statusResponse.json();
        
        if (statusResult.message === 'success') {
          setStatus(statusResult.data);
          setError('');
          
          // Immediately fetch product group with the shift info
          if (statusResult.data?.currentShift?.number) {
            await fetchActiveProductGroup(statusResult.data.currentShift.number);
          }
        }
      } catch (err) {
        console.error('Error initializing ProductionStatus:', err);
        setError('Connection error');
      } finally {
        setLoading(false);
      }
    };
    
    initializeComponent();
    
    // Set up interval to fetch every 2 seconds for better performance
    const statusInterval = setInterval(fetchProductionStatus, 2000);
    
    // Cleanup intervals on component unmount
    return () => {
      clearInterval(statusInterval);
    };
  }, [props.productGroup, props.lineMode, props.currentShift, props.productionDate, props.currentTime, apiUrl, selectedLineId]);
  
  // CRITICAL: Additional effect to watch for prop changes specifically for production timer
  useEffect(() => {
    if (props.productGroup) {
      console.log('ProductionStatus: Props changed - productionTime:', props.productGroup.productionTimeSeconds || props.productGroup.productionTime || 0);
      // Force update by creating new object reference to trigger re-render
      const updatedProductGroup = {...props.productGroup};
      setActiveProductGroup(updatedProductGroup);
    }
  }, [props.productGroup?.productionTimeSeconds, props.productGroup?.productionTime, props.productGroup?.id]);
  
  // Don't fetch on mount - wait for status to be available
  // This prevents premature 'stop' mode setting
  
  // Separate interval for Product Group updates (only if not using props)
  useEffect(() => {
    // Skip if props are provided
    if (props.productGroup || props.lineMode) {
      return;
    }
    
    if (status?.currentShift?.number) {
      const productGroupInterval = setInterval(() => {
        fetchActiveProductGroup();
      }, 2000); // 2-second interval for consistent updates
      
      return () => clearInterval(productGroupInterval);
    }
  }, [status?.currentShift?.number, apiUrl, selectedLineId, props.productGroup, props.lineMode]);
  
  // Fetch Product Group when shift is available or line changes (only if not using props)
  useEffect(() => {
    // Skip if props are provided
    if (props.productGroup || props.lineMode) {
      return;
    }
    
    if (status?.currentShift?.number) {
      fetchActiveProductGroup();
    }
  }, [status?.currentShift?.number, selectedLineId, props.productGroup, props.lineMode]);
  
  // Remove this useEffect - mode is now set directly in fetchActiveProductGroup
  // This prevents race conditions and ensures single source of truth
  
  // Log shift changes for debugging
  useEffect(() => {
    if (status?.currentShift) {
      console.log(`Current shift: ${status.currentShift.name}`);
    }
  }, [status?.currentShift]);
  
  
  // Format time from seconds to hours, minutes, seconds
  const formatTimeFromSeconds = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return { hours, minutes, seconds };
  };
  
  // Format time as HH:MM:SS for production time display
  const formatProductionTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (duration: { hours: number; minutes: number; seconds?: number } | null) => {
    if (!duration) return 'N/A';
    const h = duration.hours;
    const m = duration.minutes;
    const s = duration.seconds || 0;
    
    if (h > 0) {
      return `${h}h ${m > 0 ? `${m}m` : ''} ${s > 0 ? `${s}s` : ''}`.trim();
    } else if (m > 0) {
      return `${m}m ${s}s`;
    } else {
      return `${s}s`;
    }
  };

  const formatDate = (dateStr: string) => {
    // Handle date strings in YYYY-MM-DD format to avoid timezone conversion issues
    // Split the date string and construct date in local timezone
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-based
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    // Fallback for other date formats
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getShiftStatusColor = (shift: ProductionStatusData['currentShift']) => {
    if (!shift.isActive) {
      return {
        bg: 'bg-gray-100',
        border: 'border-gray-300',
        text: 'text-gray-700',
        dot: 'bg-gray-500'
      };
    }
    
    switch (shift.number) {
      case 1:
        return {
          bg: 'bg-blue-100',
          border: 'border-blue-300',
          text: 'text-blue-800',
          dot: 'bg-blue-500'
        };
      case 2:
        return {
          bg: 'bg-green-100',
          border: 'border-green-300',
          text: 'text-green-800',
          dot: 'bg-green-500'
        };
      default:
        return {
          bg: 'bg-gray-100',
          border: 'border-gray-300',
          text: 'text-gray-700',
          dot: 'bg-gray-500'
        };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600">Loading production status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-red-700 font-medium">{error}</span>
        </div>
      </div>
    );
  }

  const getModeConfig = (mode: string) => {
    switch (mode) {
      case '':
        // Loading state - don't show stop prematurely
        return { name: 'Loading...', color: 'text-gray-500', bg: 'bg-gray-100', dot: 'bg-gray-400' };
      case 'stop':
        return { name: 'Stop Mode', color: 'text-red-600', bg: 'bg-red-100', dot: 'bg-red-500' };
      case 'prepare':
        return { name: 'Prepare Mode', color: 'text-yellow-600', bg: 'bg-yellow-100', dot: 'bg-yellow-500' };
      case 'production':
        return { name: 'Production Mode', color: 'text-green-600', bg: 'bg-green-100', dot: 'bg-green-500' };
      case 'production-standby':
        return { name: 'Production Standby', color: 'text-amber-600', bg: 'bg-amber-100', dot: 'bg-amber-500' };
      case 'clean':
        return { name: 'Clean Mode', color: 'text-blue-600', bg: 'bg-blue-100', dot: 'bg-blue-500' };
      default:
        return { name: 'Unknown', color: 'text-gray-600', bg: 'bg-gray-100', dot: 'bg-gray-500' };
    }
  };

  if (!status) return null;

  const colors = getShiftStatusColor(status.currentShift);
  const modeConfig = getModeConfig(lineMode);

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Thin blue bar with Product Group info */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 py-1.5 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {activeProductGroup ? (
              <>
                <span className="text-xs font-medium opacity-90">Product Group:</span>
                <span className="font-bold text-sm">{activeProductGroup.groupName}</span>
                {activeProductGroup.lineName && (
                  <>
                    <span className="text-xs opacity-70">|</span>
                    <span className="text-xs">Line: {activeProductGroup.lineName}</span>
                  </>
                )}
              </>
            ) : (
              <span className="text-xs italic opacity-90">No Product Group Selected</span>
            )}
          </div>
          <span className="text-xs opacity-90">{status.currentDateTime}</span>
        </div>
      </div>

      {/* Compact single row of information */}
      <div className="px-3 py-2">
        <div className="flex items-center justify-between">
          {/* Left section - Date and Shift */}
          <div className="flex items-center space-x-4">
            {/* Production Date */}
            <div className="flex items-center space-x-2">
              <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium text-gray-700">{formatDate(status.productionDate)}</span>
            </div>

            {/* Current Shift */}
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div className={`w-2 h-2 rounded-full ${colors.dot}`}></div>
                {status.currentShift.isActive && (
                  <div className={`absolute inset-0 w-2 h-2 rounded-full ${colors.dot} animate-ping opacity-75`}></div>
                )}
              </div>
              <span className={`text-xs font-medium ${colors.text}`}>
                {status.currentShift.name}
              </span>
              {status.currentShift.startTime && (
                <span className="text-xs text-gray-500">
                  ({status.currentShift.startTime}-{status.currentShift.endTime})
                </span>
              )}
            </div>
          </div>

          {/* Middle section - Time tracking */}
          {status.currentShift.isActive && (
            <div className="flex items-center space-x-6">
              {/* Elapsed Time */}
              <div className="flex items-center space-x-1">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-gray-500">Elapsed:</span>
                <span className="text-xs font-bold text-gray-700 min-w-[100px] inline-block">
                  {formatDuration(status.currentShift.timeIntoShift)}/{formatDuration(status.currentShift.duration)}
                </span>
              </div>

              {/* Production Time */}
              {activeProductGroup && (
                <div className="flex items-center space-x-1">
                  <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs text-gray-500">Production:</span>
                  <span className="text-xs font-bold text-green-600 min-w-[80px] inline-block">
                    {formatProductionTime(activeProductGroup.productionTimeSeconds || activeProductGroup.productionTime || 0)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Right section - System Mode */}
          <div className="flex items-center space-x-2">
            <div className="relative">
              <div className={`w-2 h-2 rounded-full ${modeConfig.dot}`}></div>
              <div 
                className={`absolute inset-0 w-2 h-2 rounded-full ${modeConfig.dot} opacity-75`}
                style={{
                  animation: 'slowPulse 2.5s cubic-bezier(0, 0, 0.2, 1) infinite'
                }}
              ></div>
            </div>
            <span className={`text-xs font-bold ${modeConfig.color}`}>
              {modeConfig.name}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionStatus;